import { Secret, sign, SignOptions, verify } from 'jsonwebtoken';
import { err, ok, Result, ResultAsync, safeTry } from 'neverthrow';
import { backOff } from 'exponential-backoff';

import {
  BffJsonError,
  BffJwtError,
  BffMdsApiError,
  BffNetworkError,
  BffUtf8Error,
  type BffError
} from '@/server/errors';

import getEnvironmentVariables from './getEnvironmentVars';

/**
 * TODO: Fix this, it's pretty hacky
 *
 * Given an error, look through the stack trace and return the name of the file
 * from our codebase that triggered the error
 *
 * @param err The error object
 * @param defaultName A default to return if the stack doesn't contain the name of the module that threw the error
 */
const getModuleNameFromError = (error: Error | unknown, defaultName: string) => {
  if (!(error instanceof Error && error.stack)) {
    return defaultName;
  }

  const thisFile = 'safeWrappers.ts';
  const stackLines = error.stack.split('\n');
  const currentFileLine = stackLines.find((line) => line.includes(thisFile));
  const idx = stackLines.indexOf(currentFileLine || '') + 1;
  const callerFileLine = stackLines[idx];
  let moduleName = callerFileLine?.substring(callerFileLine.lastIndexOf('/') + 1);

  moduleName = moduleName?.substring(0, moduleName.indexOf('.'));

  return moduleName && moduleName.length > 0 ? moduleName : defaultName;
};

/**
 * What is this file?
 *
 * This file contains wrappers around common functions that would normally
 * throw. By using these "safe" versions of the functions, you can use them in
 * the `Result<T, E>` ecosystem without fear of random crashing.
 */

/**
 * Safe wrapper around `fetch` returning a `Result<T, E>`. This should be used
 * for outgoing all API calls in the BFF
 *
 * Example usage:
 * ```
 * import { safeFetch, responseToJson } from 'bff/safeWrappers';
 *
 * // Fetch data from the API, convert the response to json, and log
 * await safeFetch('http://example.org/api')
 *   .andThen(res => convertResponseToJson(res, moduleName))
 *   .match(
 *     value => console.log(`My json: ${value}`),
 *     error => console.error(`Something went wrong: ${error}`)
 *   );
 * ```
 */
export const safeFetch = (input: RequestInfo, init?: RequestInit) => {
  const url = typeof input === 'string' ? input : input.url;

  return ResultAsync.fromPromise(fetch(input, init), (e): BffError => {
    const errorMessage = e instanceof Error ? e.message : `Failed to fetch`;

    return {
      code: BffNetworkError.HTTPError,
      cause: `${errorMessage} (URL: ${url})`,
      moduleName: getModuleNameFromError(e, 'safeFetch')
    };
  });
};

/**
 * Wrapper for `safeFetch` for MDS API calls. It does two things:
 * - Prepends the MDS URL to the request URL
 * - Handles common error cases
 */
export const safeMdsFetch = (moduleName: string, mdsPath: string, init?: RequestInit) => {
  return safeTry(async function* () {
    const { MDS_URL } = yield* getEnvironmentVariables(['MDS_URL']).safeUnwrap();

    const response = yield* safeFetch(`${MDS_URL}/${mdsPath}`, init).safeUnwrap();

    // MDS Error handling
    if (response.status === 400) {
      return err({
        code: BffMdsApiError.ReceivedBadRequestResponse,
        cause: `MDS returned a status 400 Bad Request. The request was malformed.`,
        moduleName
      });
    }

    // Handle exceptions thrown by MDS that return a 500 status code
    if (response.status === 500) {
      const text = yield* convertResponseToText(response).safeUnwrap();

      return err({
        code: BffMdsApiError.ReceivedErrorResponse,
        cause: `MDS responded with an exception: ${text}`,
        moduleName
      });
    }

    // Donna API Gateway returns 401 when the token is invalid
    if (response.status === 401) {
      return err({
        code: BffMdsApiError.ReceivedUnauthorizedResponse,
        cause: `MDS Donna API Gateway returned a status 401 Unauthorized - Invalid Access Token.`,
        moduleName
      });
    }

    if (response.status === 404) {
      return err({
        code: BffMdsApiError.ReceivedNotFoundResponse,
        cause: `MDS returned a status 404. The requested resource was not found`,
        moduleName
      });
    }

    return ok(response);
  });
};

/**
 * Safe wrapper around `Response.json()` returning a `Result<T, E>`
 * This is analogous to calling `await res.json()` after a `fetch` call.
 *
 * Example usage:
 * ```
 * import { safeFetch, responseToJson } from 'bff/safeWrappers';
 *
 * // Fetch data from the API, convert the response to json, and log
 * await safeFetch('http://example.org/api')
 *   .andThen(res => convertResponseToJson(res, moduleName))
 *   .match(
 *     value => console.log(`My json: ${value}`),
 *     error => console.error(`Something went wrong: ${error}`)
 *   );
 * ```
 */
export const convertResponseToJson = (res: Response, moduleName: string): ResultAsync<unknown, BffError> =>
  // Rather than implement this in the exact same way as
  // `convertResponseToText`, this implementation allows us to see what the bad
  // data was directly in the logs to help improve debugging times
  convertResponseToText(res).andThen((text) => {
    return safeJsonParse(text).mapErr((_e) => ({
      code: BffJsonError.JsonParseError,
      cause: `Unable to convert fetch response to json. Response data: ${text}`,
      moduleName
    }));
  });

/**
 * Safe wrapper around `Response.text()` returning a `Result<T, E>`
 * This is analogous to calling `await res.text()` after a `fetch` call.
 *
 * Example usage:
 * ```
 * import { safeFetch, responseToText } from 'bff/safeWrappers';
 *
 * // Fetch data from the API, convert the response to json, and log
 * await safeFetch('http://example.org/api')
 *   .andThen(convertResponseToText)
 *   .match(
 *     value => console.log(`My text: ${value}`),
 *     error => console.error(`Something went wrong: ${error}`)
 *   );
 * ```
 */
export const convertResponseToText = (res: Response) =>
  ResultAsync.fromPromise(
    res.text(),
    (e): BffError => ({
      code: BffUtf8Error.TextParseError,
      cause: e instanceof Error ? e.message : 'Called .text() on the result of a `fetch` call',
      moduleName: getModuleNameFromError(e, 'convertResponseToText')
    })
  );

/**
 * A safe wrapper around JSON.parse returning a `Result<T, E>`
 *
 * Example usage:
 *
 * ```
 * import { safeJsonParse } from 'bff/safeWrappers';
 *
 * // Parse strings into JSON
 * safeJsonParse('{"name": "john"}')
 * safeJsonParse<{age: number}>('{"age": 20}')
 * ```
 */
export const safeJsonParse = <T = unknown>(...[text, reviver]: Parameters<typeof JSON.parse>): Result<T, BffError> => {
  try {
    const parsed = JSON.parse(text, reviver);

    return ok(parsed);
  } catch (e) {
    return err({
      code: BffJsonError.JsonParseError,
      cause: e instanceof Error ? e.message : 'Unknown',
      moduleName: getModuleNameFromError(e, 'safeJsonParse')
    });
  }
};

/**
 * A safe wrapper around JSON.stringify returning a `Result<T, E>`
 */
export const safeJsonStringify = Result.fromThrowable(
  JSON.stringify,
  (e): BffError => ({
    code: BffJsonError.JsonStringifyError,
    cause: e instanceof Error ? e.message : 'Unknown',
    moduleName: getModuleNameFromError(e, 'safeJsonStringify')
  })
);

/**
 * Safe wrapper around `jsonwebtoken.sign()` (from jsonwebtoken library) returning a `Result<T, E>`
 *
 * NOTE: JWTs with a specific purpose and type should use the `getJWT` function instead
 *
 * Example usage:
 * ```
 * import { safeJwtSign } from 'bff/utils/safeWrappers';
 *
 * getEnvironmentVariables(["BFF_JWT_SECRET_KEY"])
 *    .andThen(({ BFF_JWT_SECRET_KEY }) =>
 *        safeJwtSign(
 *            { foo: 'myvalue' },
 *            BFF_JWT_SECRET_KEY,
 *            { expiresIn: '1h' }
 *        );
 *    )
 * ```
 *
 * @param payload JSON object to be encoded in JWT token, can be read/used using jsonwebtoken.verify in the follow up api call
 * @param secretOrPrivateKey secret key used to encode JWT, stored in secret vault, same value need to be used for decode JWT
 * @param options Optional configuration data for encoding JWT in JSON object
 * @returns Result<token as string, BffError>
 */
export const safeJwtSign = (
  payload: string | Buffer | object,
  secretOrPrivateKey: Secret,
  options?: SignOptions
): Result<string, BffError> => {
  try {
    const token = sign(payload, secretOrPrivateKey, options);

    return ok(token);
  } catch (e) {
    let errorCause = `Unable to generate jwt using jsonwebtoken.sign with payload: ${JSON.stringify(payload)}`;

    if (!(e instanceof Error)) {
      errorCause += ', Unknown cause';
    } else {
      errorCause += `, receiving error: ${e.message}`;
    }

    return err({
      code: BffJwtError.UnableToGenerateJwt,
      cause: errorCause,
      moduleName: getModuleNameFromError(e, 'safeJwtSign')
    });
  }
};

/**
 * Safe wrapper around `jsonwebtoken.verify()` (from jsonwebtoken library) returning a `Result<T, E>`
 *
 * Example usage:
 * ```
 * import { safeJwtVerify } from 'bff/utils/safeWrappers';
 * safeJwtVerify<{ foo: string }>(
 *    'jwt',
 *    process.env.BFF_JWT_SECRET_KEY
 * );
 *
 * @param jwt
 * @param secretOrPublicKey
 * @param options
 * @returns
 */
export const safeJwtVerify = <T>(
  jwt: string,
  secretOrPublicKey: Secret,
  options?: SignOptions
): Result<T, BffError> => {
  try {
    const decodedJwt = verify(jwt, secretOrPublicKey, options);

    return ok({ ...(decodedJwt as T) });
  } catch (e) {
    let errorCause = `Unable to verify JWT using jsonwebtoken.sign: ${jwt}`;

    if (!(e instanceof Error)) {
      errorCause += ', Unknown cause';
    } else {
      errorCause += `, receiving error: ${e.message}`;
    }

    return err({
      code: BffJwtError.InvalidJwt,
      cause: errorCause,
      moduleName: getModuleNameFromError(e, 'safeJwtVerify')
    });
  }
};

/**
 * Safe wrapper around `backOff` from the `exponential-backoff` npm package:
 * https://www.npmjs.com/package/exponential-backoff
 */
export const safeBackOff = async <T>(
  ...[request, options]: Parameters<typeof backOff<T>>
): Promise<Result<T, BffError>> => {
  try {
    const ret = await backOff(request, options);

    return ok(ret);
  } catch (e) {
    return err({
      code: BffNetworkError.BackOffFailed,
      cause: e instanceof Error ? e.message : 'Unknown',
      moduleName: getModuleNameFromError(e, 'safeBackOff')
    });
  }
};
